import React from "react";
import KeyboardNavigationGroup from "./KeyboardNavigationGroup";
import { GroupedNavigationProvider } from "./GroupedNavigationContext";
import FocusManager from "../core/FocusManager";

const Group: React.FC<
  React.HTMLProps<HTMLDivElement> & {
    children: React.ReactElement<React.HTMLProps<HTMLElement>>;
  }
> = (props) => {
  const { children, ...divProps } = props;
  const [isNavigating, setIsNavigating] = React.useState(false);
  const cellRef = React.useRef<HTMLElement>(null);

  const enterNavigation = () => {
    setIsNavigating(true);
    if (cellRef.current) {
      FocusManager.setFocusForChild(
        cellRef.current,
        `:is(${FocusManager.selectors.focusable}):not([data-skip-focus])`
      );
    }
  };

  const exitNavigation = () => {
    setIsNavigating(false);
    cellRef.current?.focus();
  };

  return (
    <div
      {...divProps}
      role="row"
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
        const root = cellRef.current;
        if (!root) {
          return;
        }

        const gainedFocusEl = event.relatedTarget as Node;

        if (
          gainedFocusEl instanceof HTMLElement &&
          gainedFocusEl.hasAttribute("data-sentinel")
        ) {
          // If the focus is moving to a sentinel, we don't need to reset the navigating flag.
          return;
        }

        if (!root.contains(gainedFocusEl)) {
          // If the focus is moving outside the cell, reset the navigating flag.
          setIsNavigating(false);
        }
      }}
      onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
        const root = cellRef.current;
        if (!root) {
          return;
        }

        const gainedFocusEl = event.target;
        const lostFocusEl = event.relatedTarget as Node;

        const rootGainedFocus = root === gainedFocusEl;
        const childGainedFocus =
          root !== gainedFocusEl && root.contains(gainedFocusEl);
        const childLostFocus =
          root !== lostFocusEl && root.contains(lostFocusEl);

        if (rootGainedFocus) {
          // Ensure the navigating flag is reset. This can happen when focus
          // is restored to the cell after closing a dialog that was opened
          // from within the cell (such as a row edit dialog).
          setIsNavigating(false);
        } else if (childGainedFocus && !childLostFocus) {
          // Ensure the navigating flag is set when focus is within the cell.
          // This can happen if the mouse is used to focus elements within.
          setIsNavigating(true);
        }
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape" && isNavigating) {
          exitNavigation();
          event.stopPropagation();
          event.preventDefault();
        }
      }}
    >
      <GroupedNavigationProvider
        groupControlsFocus={!isNavigating}
        onGroupEnter={enterNavigation}
      >
        <div
          data-sentinel
          onFocus={exitNavigation}
          tabIndex={isNavigating ? 0 : undefined}
        />
        {React.cloneElement(children, {
          "aria-colindex": 1,
          ref: cellRef,
          role: "gridcell",
          tabIndex: -1,
        })}
        <div
          data-sentinel
          onFocus={exitNavigation}
          tabIndex={isNavigating ? 0 : undefined}
        />
      </GroupedNavigationProvider>
    </div>
  );
};

export interface GroupedNavigationRef {
  restoreLostFocus: () => void;
}

export const GroupedNavigation = React.forwardRef<
  GroupedNavigationRef,
  React.HTMLAttributes<HTMLDivElement> & {
    children: React.ReactElement<React.HTMLProps<HTMLElement>>[];
    firstRowIndex: number;
    totalRows: number;
  }
>((props, ref) => {
  const { children, firstRowIndex, totalRows, ...divProps } = props;

  const lastFocusedChild = React.useRef<HTMLElement | null>(null);

  React.useImperativeHandle(ref, () => ({
    restoreLostFocus: () => {
      lastFocusedChild.current?.focus();
    },
  }));

  return (
    <KeyboardNavigationGroup
      {...divProps}
      aria-colcount={1}
      aria-rowcount={totalRows}
      childSelector="div[role=gridcell]"
      onFocusChanged={(lastFocused) => {
        lastFocusedChild.current = lastFocused;
      }}
      role="grid"
      upDown
    >
      {children.map((child, index) => (
        <Group aria-rowindex={firstRowIndex + index + 1} key={index}>
          {child}
        </Group>
      ))}
    </KeyboardNavigationGroup>
  );
});
